/**
 * NotesSectionContainer - Manages player notes state with sharing support
 *
 * Container component that handles state and callbacks for the player
 * notes section using real data from GraphQL. Resolves author display
 * names from Cognito user attributes embedded in the NotesNote fragment,
 * showing "You" for current user's notes. Manages share sheet state and
 * fetches share counts for owned notes to display sharing badges.
 * Auto-shares newly created notes with organization managers and admins
 * via the shared useAutoShareWithManagers hook.
 * Initializes note visibility toggle from the organization's
 * defaultNoteVisibility setting instead of hardcoding false.
 * Requests a large page size (NOTES_PAGE_SIZE) to avoid the backend's
 * default 20-item limit silently truncating results (SE-4007).
 *
 * Reads `error` and `loading` off the notes query and hands the card a single
 * read state (SE-7872). Before that, only `data` and `refetch` were
 * destructured, so a failed read arrived as no notes and the card said the
 * player had none — a claim the data never supported. The empty state is
 * unchanged; it simply no longer stands in for a failure.
 *
 * Share counts are sourced from the Apollo cache reactively via
 * `useReactiveNoteShareInfoMap` (SE-4289). When `NoteShareSheetContainer`
 * performs a `cache.modify` after share/revoke, the watchQuery feeding
 * this map fires and the count updates without an imperative refetch.
 */
import { NetworkStatus, useApolloClient } from "@apollo/client";
import * as Device from "expo-device";
import { useCallback, useEffect, useMemo, useState } from "react";

import NotesSectionView from "./NotesSectionView";

import type { PlayerNote } from "@/features/player-detail/types";

import { AnalyticsTracker } from "@/features/analytics";
import { useCurrentUser } from "@/features/auth";
import useAutoShareWithManagers from "@/features/notes/hooks/useAutoShareWithManagers";
import { useReactiveNoteShareInfoMap } from "@/features/notes/hooks/useReactiveNoteShareInfoMap";
import { handleCreateOrDeleteNote } from "@/features/notes/utils/handleDataNote";
import {
  deriveNotesReadState,
  isNotesReadError,
} from "@/features/player-detail/utils/reportsNotesReadState";
import {
  useNotesListNotesByPlayerIdQuery,
  useNotesCreateNoteMutation,
  useNotesDeleteNoteMutation,
  useNotesShareNoteMutation,
  NotesListNoteSharesDocument,
  type NotesListNotesByPlayerIdQuery,
  type NotesListNoteSharesQuery,
  type NotesListNoteSharesQueryVariables,
} from "@/generated/graphql";
import { useAppLocale } from "@/i18n";
import { useTheme } from "@/providers/ThemeProvider";
import { playerAutoCreateNoteVar } from "@/stores/player";
import { getCognitoDisplayName } from "@/utils/getCognitoDisplayName";
import {
  MEDIUM_DATE_OPTIONS,
  formatDate as formatIntlDate,
} from "@/utils/intlFormat";
import { ifWeb } from "@/utils/platform";

/** Maximum notes to display before "View all" */
const MAX_VISIBLE_NOTES = 4;

/** Safety-belt caps for the inline note input (SE-5208) — body raised from 300 */
const NOTE_TITLE_MAX_LENGTH = 100;
const NOTE_BODY_MAX_LENGTH = 5000;

/**
 * Maximum notes to request from the backend in a single page —
 * the default server page size is 20, which silently truncates
 * results for players with many notes (SE-4007)
 */
const NOTES_PAGE_SIZE = 200;

/**
 * The ids of the notes in a notes-list result.
 * @param result - A `listNotesByPlayerId` query result
 * @returns The note ids it contains
 */
const noteIdsOf = (
  result: NotesListNotesByPlayerIdQuery | undefined
): ReadonlySet<string> =>
  new Set((result?.listNotesByPlayerId?.edges ?? []).map(edge => edge.node.id));

/**
 * Props for NotesSectionContainer component
 */
interface NotesSectionContainerProps {
  /** Player ID for fetching notes */
  readonly playerId?: string;
  /**
   * Whether the card is one of two side-by-side columns rather than a block in
   * a stack. Threaded straight to the view, which uses it to decide whether the
   * card may take a zero flex basis (SE-6910).
   */
  readonly isSideBySide?: boolean;
}

/**
 * Open-state for the note editor modal (SE-5519). `null` means the editor is
 * closed; otherwise it carries the mode and, for edits, the target note id.
 * This single value replaces the former `isAddingNote` boolean +
 * `editingNoteId` pair so add/edit are one modal driven by one state.
 */
type NoteEditorState =
  | { readonly mode: "add" }
  | { readonly mode: "edit"; readonly noteId: string };

/**
 * Format a note's created date for display, in the active UI locale.
 *
 * SE-7966: the locale was hardcoded `"en-US"`; it is now a parameter so
 * the note list follows the account's language.
 * @param dateString - ISO date string
 * @param locale - The active UI locale
 * @returns Formatted date string, or the raw value when unparseable
 */
const formatNoteDate = (dateString: string, locale: string): string =>
  formatIntlDate(dateString, locale, MEDIUM_DATE_OPTIONS) || dateString;

/**
 * NotesSectionContainer manages state for player notes section with sharing
 * @param props - Container props
 * @param props.playerId - Player ID for fetching notes
 * @param props.isSideBySide - Whether the card is one of two side-by-side columns
 * @returns NotesSectionView with state and callbacks
 */
const NotesSectionContainer = ({
  playerId,
  isSideBySide,
}: NotesSectionContainerProps) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const currentUser = useCurrentUser();
  const apolloClient = useApolloClient();

  const defaultNoteVisibility =
    currentUser?.organization?.defaultNoteVisibility ?? false;
  const [noteInput, setNoteInput] = useState("");
  const [showViewAllModal, setShowViewAllModal] = useState(false);
  const [editorState, setEditorState] = useState<NoteEditorState | null>(() =>
    // Read the auto-create signal WITHOUT mutating — a pure initializer stays
    // correct under React Strict Mode's dev double-invoke (a mutating
    // initializer would clear the var on the first pass, so the second pass
    // sees false and the editor never opens). The var is reset in a mount
    // effect below to preserve the one-shot semantics.
    playerAutoCreateNoteVar() ? { mode: "add" } : null
  );
  const [isPublic, setIsPublic] = useState(defaultNoteVisibility);
  const [shareNoteId, setShareNoteId] = useState<string | null>(null);

  const isShareSheetOpen = shareNoteId !== null;
  const isEditorOpen = editorState !== null;
  const editorMode = editorState?.mode ?? "add";
  const editingNoteId =
    editorState?.mode === "edit" ? editorState.noteId : null;

  // SE-7872: `error` and `loading` are read here — they never were, which is
  // why a failed notes read rendered identically to a player with no notes.
  // `notifyOnNetworkStatusChange` makes `loading` stay true across refetches
  // as well as the first fetch, so a retry with nothing on screen shows
  // progress rather than flashing the empty card.
  const {
    data,
    loading: notesLoading,
    error: notesError,
    networkStatus,
    refetch,
  } = useNotesListNotesByPlayerIdQuery({
    variables: { playerId: playerId ?? "", first: NOTES_PAGE_SIZE },
    skip: !playerId,
    notifyOnNetworkStatusChange: true,
  });

  const isRefreshingNotes = networkStatus === NetworkStatus.refetch;

  /**
   * Retries the notes read from the failed/stale card. `refetch` rejects on a
   * second failure, which would otherwise surface as an unhandled rejection;
   * swallowing it here leaves the error state on screen, which is the intended
   * outcome — the next render derives `failed` again from the hook's `error`.
   */
  const handleRetryNotes = useCallback(() => {
    void refetch().catch(() => {});
  }, [refetch]);

  /**
   * Refetches the notes until the list reflects a write this card just made.
   *
   * `refetch()` does not always send a request: Apollo deduplicates it onto an
   * identical read already in flight. A note saved while the first load is
   * still pending (4.9s on dev in run 36145421764) therefore gets back THAT
   * load's response — issued before the write — which replaces the list, so
   * the just-saved note vanishes and nothing reads again (SE-8547). The second
   * refetch starts after the in-flight read has settled, so it is a fresh one.
   * @param isReflected - Whether a list with these note ids shows the write
   */
  const refetchReflecting = useCallback(
    async (
      isReflected: (ids: ReadonlySet<string>) => boolean
    ): Promise<void> => {
      const first = await refetch();
      if (isReflected(noteIdsOf(first.data))) return;
      await refetch();
    },
    [refetch]
  );

  const [createNote] = useNotesCreateNoteMutation();
  const [deleteNote] = useNotesDeleteNoteMutation();
  const [shareNote] = useNotesShareNoteMutation();
  const { shareNoteWithManagers, managerUserIds } = useAutoShareWithManagers();

  const resetNoteForm = useCallback(() => {
    setNoteInput("");
    setEditorState(null);
    setIsPublic(defaultNoteVisibility);
  }, [defaultNoteVisibility]);

  const handleStartAddNote = useCallback(() => {
    setNoteInput("");
    setIsPublic(defaultNoteVisibility);
    setEditorState({ mode: "add" });
  }, [defaultNoteVisibility]);

  const locale = useAppLocale();

  const rawNotes = useMemo(() => {
    const edges = data?.listNotesByPlayerId?.edges ?? [];
    return edges.map(edge => ({
      id: edge.node.id,
      category: "General" as const,
      date: formatNoteDate(edge.node.createdAt, locale),
      content: edge.node.body || edge.node.title,
      isPrivate: !edge.node.isPublic,
      userId: edge.node.userId,
      authorName:
        edge.node.userId === currentUser?.id
          ? "You"
          : getCognitoDisplayName(
              edge.node.user?.cognitoUser?.Attributes ??
                edge.node.user?.cognitoUser?.UserAttributes
            ),
    }));
  }, [data?.listNotesByPlayerId?.edges, currentUser?.id, locale]);

  const ownedNoteIds = useMemo(
    (): ReadonlySet<string> =>
      new Set(
        rawNotes
          .filter(note => note.userId === currentUser?.id)
          .map(note => note.id)
      ),
    [rawNotes, currentUser?.id]
  );

  const managerIdSet = useMemo(() => new Set(managerUserIds), [managerUserIds]);

  const canShareNotes = !defaultNoteVisibility;

  /**
   * Reactive share-info map driven by Apollo `cache.watch`. Updates
   * automatically when `NoteShareSheetContainer` performs a `cache.modify`
   * for share/revoke — no manual refetch needed.
   */
  const shareInfoMap = useReactiveNoteShareInfoMap(
    ownedNoteIds,
    managerIdSet,
    canShareNotes
  );

  const allNotes = useMemo(
    (): readonly PlayerNote[] =>
      rawNotes.map(note => ({
        ...note,
        shareCount: shareInfoMap[note.id]?.shareCount ?? 0,
        isSharedWithMe:
          shareInfoMap[note.id]?.isSharedWithMe ??
          (note.userId !== currentUser?.id && note.isPrivate),
      })),
    [rawNotes, shareInfoMap, currentUser?.id]
  );

  const visibleNotes = useMemo(
    () => allNotes.slice(0, MAX_VISIBLE_NOTES),
    [allNotes]
  );

  // SE-7872: the one state the card renders from. `!playerId` counts as
  // pending because the query is skipped then — an absent result while the
  // player is still resolving is not evidence that the player has no notes.
  /**
   * Whether the hook's error is about READING THE NOTES, or about a field
   * hanging off a note that arrived fine.
   *
   * The classification is a pure function, and NOT the obvious one — it
   * cannot be answered by asking whether the rows survived. Under this app's
   * `errorPolicy: "all"`, a GraphQL failure on this non-nullable field hands
   * back the PREVIOUS cached rows with `networkError === null`, so surviving
   * rows are the normal state after a failed refetch rather than evidence of
   * a successful one. `utils/reportsNotesReadState.ts` carries the Apollo
   * mechanism and the fail-closed contract.
   */
  const hasNotesReadError = useMemo(
    () => isNotesReadError(notesError),
    [notesError]
  );

  const readState = useMemo(
    () =>
      deriveNotesReadState({
        noteCount: allNotes.length,
        isPending: !playerId || notesLoading,
        hasError: hasNotesReadError,
      }),
    [allNotes.length, playerId, notesLoading, hasNotesReadError]
  );

  const handleNoteInputChange = useCallback((text: string) => {
    setNoteInput(text);
  }, []);

  const handleNoteSubmit = useCallback(async () => {
    if (!noteInput.trim() || !playerId) return;

    const mutationInput = {
      playerId,
      title: noteInput.substring(0, NOTE_TITLE_MAX_LENGTH),
      body: noteInput.substring(0, NOTE_BODY_MAX_LENGTH),
      isPublic,
    };

    try {
      if (editingNoteId) {
        // Update by creating new note first, then deleting old one
        // (workaround for backend updateNote mutation returning null)
        // Create-first ensures original note is preserved if create fails
        const originalNote = data?.listNotesByPlayerId?.edges?.find(
          edge => edge.node.id === editingNoteId
        )?.node;

        if (!originalNote) {
          console.warn("Could not find original note to update");
          resetNoteForm();
          return;
        }

        // Fetch existing shares before creating new note (SE-4104)
        const sharesResult = await apolloClient.query<
          NotesListNoteSharesQuery,
          NotesListNoteSharesQueryVariables
        >({
          query: NotesListNoteSharesDocument,
          variables: { noteId: editingNoteId },
          fetchPolicy: "network-only",
        });

        const createResult = await createNote({
          variables: { input: mutationInput },
        });

        const newNoteId = createResult.data?.createNote?.id;

        // Transfer shares from old note to new note (SE-4104)
        const existingShares = sharesResult.data?.listNoteShares ?? [];
        if (newNoteId) {
          await Promise.all(
            existingShares
              .filter(s => s.role !== "OWNER")
              .map(s =>
                shareNote({
                  variables: {
                    input: { noteId: newNoteId, targetUserId: s.userId },
                  },
                })
              )
          );
        }

        // Only delete old note after successful create and share transfer
        await deleteNote({
          variables: { input: { id: editingNoteId } },
        });

        resetNoteForm();
        await refetchReflecting(
          ids => (!newNoteId || ids.has(newNoteId)) && !ids.has(editingNoteId)
        );
      } else {
        // SE-5577: seed the created note into `listNotesByPlayerId`
        // synchronously so a note authored by the current user renders its
        // edit/delete affordances from FIRST paint. Without an
        // optimisticResponse + cache `update`, the note is absent from the list
        // (and therefore from `ownedNoteIds`) until the trailing `refetch()`
        // resolves — briefly painting an owned note with no action row. Ports
        // the sibling NotesForm pattern (handleCreateOrDeleteNote). The
        // optimisticResponse MUST be COMPLETE against the NotesNote fragment —
        // an incomplete one triggers "Missing field" cache errors, which is
        // worse than none. `userId`/`user.id` = currentUser.id is what makes
        // `ownedNoteIds` include it on frame one; `refetchReflecting` below
        // then reconciles server truth.
        const nowIso = new Date().toISOString();
        const result = await createNote({
          variables: { input: mutationInput },
          optimisticResponse: {
            __typename: "Mutation",
            createNote: {
              __typename: "Note",
              id: `temp-id-${nowIso}`,
              createdAt: nowIso,
              updatedAt: nowIso,
              title: mutationInput.title,
              body: mutationInput.body,
              isPublic: mutationInput.isPublic,
              playerId,
              userId: currentUser?.id ?? "",
              user: {
                __typename: "User",
                id: currentUser?.id ?? "",
                cognitoUser: null,
              },
            },
          },
          update: (cache, { data, errors }) =>
            handleCreateOrDeleteNote("create", cache, data, errors, playerId),
        });
        const createdNote = result.data?.createNote;
        const noteId = createdNote?.id;
        if (noteId) {
          await shareNoteWithManagers(noteId, "NotesSection");

          // SE-5224: emit the note_created PostHog event. The logger existed
          // but was never invoked, so PostHog received zero of these events.
          // Only the genuine create path fires it — the edit path's internal
          // create-then-delete (SE-4104) must not inflate the count.
          AnalyticsTracker.note
            .logNoteCreated({
              user_id: currentUser?.id ?? "",
              organization_id: currentUser?.organizationId ?? "",
              note_type: "General",
              content_length: mutationInput.body.length,
              phone_number: currentUser?.phoneNumber ?? "",
              note_id: noteId,
              player_id: createdNote?.playerId ?? playerId,
              title: createdNote?.title ?? mutationInput.title,
              body: createdNote?.body ?? mutationInput.body,
              is_shared: managerUserIds.length > 0,
              device_name: Device?.modelName ?? "",
              device_os: Device?.osName ?? "",
              creation_source: "NotesSection",
              platform: ifWeb("web", "mobile"),
              created_at: createdNote?.createdAt ?? nowIso,
              updated_at: createdNote?.updatedAt ?? nowIso,
            })
            .catch((analyticsError: unknown) => {
              console.warn(
                "[Notes] note_created analytics error:",
                analyticsError
              );
            });
        }

        resetNoteForm();
        await refetchReflecting(ids => !noteId || ids.has(noteId));
      }
    } catch (error) {
      console.warn("Failed to save note:", error);
      // Only clear form when editing (content came from existing note)
      // For new notes, preserve input so user can retry
      if (editingNoteId) {
        resetNoteForm();
      }
    }
  }, [
    noteInput,
    playerId,
    editingNoteId,
    isPublic,
    data,
    createNote,
    deleteNote,
    shareNote,
    apolloClient,
    refetchReflecting,
    resetNoteForm,
    shareNoteWithManagers,
    currentUser,
    managerUserIds.length,
  ]);

  const handleViewAll = useCallback(() => {
    setShowViewAllModal(true);
  }, []);

  const handleCloseViewAll = useCallback(() => {
    setShowViewAllModal(false);
  }, []);

  const handleCancelEdit = useCallback(() => {
    resetNoteForm();
  }, [resetNoteForm]);

  const handleEditNote = useCallback(
    (noteId: string) => {
      const noteToEdit = allNotes.find(note => note.id === noteId);
      if (noteToEdit) {
        setNoteInput(noteToEdit.content);
        setIsPublic(!noteToEdit.isPrivate);
        setEditorState({ mode: "edit", noteId });
      }
    },
    [allNotes]
  );

  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      try {
        const deleteResult = await deleteNote({
          variables: {
            input: { id: noteId },
          },
        });

        // SE-5224: emit the note_deleted PostHog event. The logger existed but
        // was never invoked, so PostHog received zero of these events. This is
        // intentionally only the genuine, user-initiated delete handler — the
        // edit path's internal create-then-delete (SE-4104) does NOT route
        // through here, so update-as-delete never inflates the delete count.
        const deletedNote = deleteResult.data?.deleteNote;
        if (deletedNote) {
          const nowIso = new Date().toISOString();
          AnalyticsTracker.note
            .logNoteDeleted({
              note_id: deletedNote.id,
              player_id: deletedNote.playerId ?? playerId ?? "",
              title: deletedNote.title ?? "",
              body: deletedNote.body ?? "",
              is_public: deletedNote.isPublic ?? false,
              created_at: deletedNote.createdAt ?? nowIso,
              updated_at: deletedNote.updatedAt ?? nowIso,
            })
            .catch((analyticsError: unknown) => {
              console.warn(
                "[Notes] note_deleted analytics error:",
                analyticsError
              );
            });
        }

        await refetchReflecting(ids => !ids.has(noteId));
      } catch (error) {
        console.warn("Failed to delete note:", error);
      }
    },
    [deleteNote, refetchReflecting, playerId]
  );

  const handleShareNote = useCallback((noteId: string) => {
    setShareNoteId(noteId);
  }, []);

  const handleShareSheetClose = useCallback(() => {
    setShareNoteId(null);
  }, []);

  /**
   * No-op kept for backwards compat with NoteShareSheetContainer's
   * `onShareChange` prop. The reactive cache subscription updates
   * `shareInfoMap` automatically; we only need this hook so the share
   * sheet's existing call site does not break.
   */
  const handleShareChange = useCallback(() => {
    // Reactive cache subscription handles count updates — nothing to do
  }, []);

  // Consume the one-shot auto-create signal exactly once after mount. Clearing
  // a reactive var here (not setState) is safe: under Strict Mode's dev
  // double-invoke the var is already false on the second setup, so this is a
  // no-op then; in production it fires once so a later remount won't re-open
  // the editor. Kept out of the state initializer to keep that pure.
  useEffect(() => {
    if (playerAutoCreateNoteVar()) {
      playerAutoCreateNoteVar(false);
    }
  }, []);

  // Debug: log org note visibility for testing SE-4100
  useEffect(() => {
    console.info("[Notes] Org visibility:", {
      defaultNoteVisibility,
      canShareNotes,
      orgName: currentUser?.organization?.name,
    });
  }, [defaultNoteVisibility, canShareNotes, currentUser?.organization?.name]);

  return (
    <NotesSectionView
      isDark={isDark}
      isSideBySide={isSideBySide}
      notes={visibleNotes}
      allNotes={allNotes}
      noteCount={allNotes.length}
      noteInput={noteInput}
      showViewAllModal={showViewAllModal}
      onNoteInputChange={handleNoteInputChange}
      onNoteSubmit={handleNoteSubmit}
      onViewAll={handleViewAll}
      onCloseViewAll={handleCloseViewAll}
      onEditNote={handleEditNote}
      onDeleteNote={handleDeleteNote}
      isEditorOpen={isEditorOpen}
      editorMode={editorMode}
      editingNoteId={editingNoteId}
      onCancelEdit={handleCancelEdit}
      ownedNoteIds={ownedNoteIds}
      onStartAddNote={handleStartAddNote}
      isPublic={isPublic}
      onToggleVisibility={setIsPublic}
      onShareNote={handleShareNote}
      shareNoteId={shareNoteId}
      isShareSheetOpen={isShareSheetOpen}
      onShareSheetClose={handleShareSheetClose}
      onShareChange={handleShareChange}
      canShareNotes={canShareNotes}
      readState={readState}
      onRetry={handleRetryNotes}
      isRetrying={isRefreshingNotes}
    />
  );
};

export default NotesSectionContainer;
